import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

const DEFAULT_DAYJS_LOCALE = 'en'

/**
 * @description Given a locale, returns the dayjs locale object.
 * This is to dynamically load the dayjs locale object needed
 * and not to load the entire dayjs library.
 * @param {string} [localeContext='en-US'] - Locale to load.
 * @default 'en'
 * @returns {Locale} Requested (or `en` if localeContext not passed) dayjs locale.
 */
export const useDayJsLocale = (localeContext = DEFAULT_DAYJS_LOCALE) => {
	const [locale, setLocale] = useState()

	useEffect(() => {
		const importLocaleFile = async () => {
			try {
				// Try to load the correct locale file.
				const dayjsLocale = localeContext.toLocaleLowerCase()
				if (dayjsLocale === DEFAULT_DAYJS_LOCALE) {
					setLocale(DEFAULT_DAYJS_LOCALE)
					return // skip en locale module download because it's already included in dayjs
				}
				const localeToSet = await import(`dayjs/locale/${dayjsLocale}.js`)
				dayjs.locale(localeToSet.default, null, true) // add locale object to dayjs
				setLocale(dayjsLocale)
			} catch (error) {
				try {
					/**
					 * If the locale file doesn't exist, try to load the default locale file.
					 * This is to support the case where the locale file is not available.
					 * For example, the locale file for `it-IT` is not available, but `it` is.
					 */
					const dayjsLocale = localeContext.split('-')[0]
					if (dayjsLocale === DEFAULT_DAYJS_LOCALE) {
						setLocale(DEFAULT_DAYJS_LOCALE)
						return // skip en locale module download because it's already included in dayjs
					}
					const localeToSet = await import(`dayjs/locale/${dayjsLocale}.js`)
					dayjs.locale(localeToSet.default, null, true) // add locale object to dayjs
					setLocale(dayjsLocale)
				} catch (error) {
					// If the default locale file also does not exist, load the English locale file.
					if (process.env.NODE_ENV !== 'production') {
						console.warn(
							`Error loading date-locale file: ${
								localeContext.split('-')[0]
							}.\nFalling down to: ${DEFAULT_DAYJS_LOCALE}.`
						)
					}
					// skip en locale module download because it's already included in dayjs
					setLocale(DEFAULT_DAYJS_LOCALE)
				}
			}
		}
		if (!locale) importLocaleFile()
	}, [locale, localeContext])

	return locale
}
